import { auth } from "../components/auth/firebase/firebase";
import { FRONTEND_CONFIG } from "../config/environment";

// Types for API responses
interface ApiResponse<T = any> {
  success: boolean;
  error?: string;
  message?: string;
}

interface AuthStatusResponse extends ApiResponse {
  user: UserInfo;
}

interface CustomerProfileResponse extends ApiResponse {
  customer: CustomerProfile;
}

interface UserInfo {
  uid: string;
  email: string;
  emailVerified: boolean;
  authenticated: true;
  customer: {
    tier: string;
    features: string[];
    displayName: string | null;
    preferences: {
      emailNotifications: boolean;
      contentUpdates: boolean;
      marketingEmails: boolean;
      timezone: string;
    };
  } | null;
}

interface CustomerProfile {
  uid: string;
  email: string;
  displayName: string | null;
  emailVerified: boolean;
  createdAt: string;
  lastLogin: string | null;
  tier: string;
  features: string[];
  purchases: Array<{
    id: string;
    sku: string;
    purchaseDate: string;
    status: string;
  }>;
  preferences: {
    emailNotifications: boolean;
    contentUpdates: boolean;
    marketingEmails: boolean;
    timezone: string;
  };
}

interface TierDefinition {
  id: string;
  name: string;
  price: number;
  priceDisplay: string;
  description: string;
  sku: string;
  popular?: boolean;
  subtitle?: string;
  buttonText: string;
  buttonStyle: "primary" | "secondary";
  features: string[];
  displayFeatures: string[];
  inherits?: string[];
}

interface TiersResponse extends ApiResponse {
  tiers: TierDefinition[];
}

// Backend download types (matching backend/src/types/downloads.ts)
export type DownloadType =
  | "bookpdf"
  | "bookepub"
  | "samplecode"
  | "bonus"
  | "template";

export interface UserDownload {
  id: string;
  type: DownloadType;
  title: string;
  description: string;
  changelog: string;
  version: string;
  fileSize: number;
  fileName: string;
  updatedAt: string;
}

interface DownloadsResponse extends ApiResponse {
  downloads: UserDownload[];
  tier: string;
}

interface DownloadUrlResponse extends ApiResponse {
  downloadUrl: string;
  expiresIn: string;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = FRONTEND_CONFIG.backend.baseUrl;
  }

  /**
   * Get Firebase ID token for authentication with automatic refresh
   */
  private async getAuthToken(): Promise<string | null> {
    if (!auth?.currentUser) {
      throw new Error("User not authenticated");
    }

    try {
      // Force refresh if token is close to expiring (within 5 minutes)
      const token = await auth.currentUser.getIdToken(true);
      return token;
    } catch (error) {
      // If token refresh fails, user needs to re-authenticate
      if (
        (error as any)?.code === "auth/user-token-expired" ||
        (error as any)?.code === "auth/invalid-user-token"
      ) {
        // Force sign out to trigger re-authentication
        await auth.signOut();
        throw new Error("Session expired. Please sign in again.");
      }

      throw new Error("Failed to get authentication token");
    }
  }

  /**
   * Make authenticated request to backend API with token refresh retry
   */
  private async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${this.baseUrl}/api${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle token expiration errors from backend
        if (
          response.status === 401 &&
          (data.error?.includes("token") ||
            data.error?.includes("expired") ||
            data.error?.includes("authenticate"))
        ) {
          await auth?.signOut();
          throw new Error("Session expired. Please sign in again.");
        }

        throw new Error(data.error || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get current user authentication status and info
   */
  async getAuthStatus(): Promise<UserInfo> {
    const response = await this.makeAuthenticatedRequest<AuthStatusResponse>(
      "/auth/me"
    );

    if (!response.success || !response.user) {
      throw new Error("Failed to get user info");
    }

    return response.user;
  }

  /**
   * Get customer profile data
   */
  async getCustomerProfile(): Promise<CustomerProfile> {
    const response =
      await this.makeAuthenticatedRequest<CustomerProfileResponse>(
        "/customers/profile"
      );

    if (!response.success || !response.customer) {
      throw new Error("Failed to get customer profile");
    }

    return response.customer;
  }

  /**
   * Get tier definitions (public endpoint)
   */
  async getTierDefinitions(): Promise<TierDefinition[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/tiers`);
      const data = (await response.json()) as TiersResponse;

      if (!data.success || !data.tiers) {
        throw new Error("Failed to get tier definitions");
      }

      return data.tiers;
    } catch (error) {
      throw new Error("Failed to retrieve tier definitions");
    }
  }

  /**
   * Get available downloads for current user's tier
   */
  async getAvailableDownloads(): Promise<any> {
    const response = await this.makeAuthenticatedRequest<any>(
      "/downloads/available"
    );

    if (!response.success) {
      throw new Error("Failed to get available downloads");
    }

    return response;
  }

  /**
   * Generate download URL for specific content item
   */
  async generateDownloadUrl(itemId: string): Promise<any> {
    const response = await this.makeAuthenticatedRequest<any>(
      `/downloads/content/${itemId}`
    );

    if (!response.success) {
      throw new Error("Failed to generate download URL");
    }

    return response;
  }

  /**
   * Legacy: Generate download URL for book by format
   */
  async getBookDownload(format: "pdf" | "epub"): Promise<any> {
    const response = await this.makeAuthenticatedRequest<any>(
      `/downloads/book/${format}`
    );

    if (!response.success) {
      throw new Error("Failed to get book download");
    }

    return response;
  }

  /**
   * Test if API is accessible
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw new Error("API is not accessible");
    }
  }

  /**
   * Check customer's upgrade eligibility
   */
  async checkUpgradeEligibility(): Promise<any> {
    const response = await this.makeAuthenticatedRequest<any>(
      "/customers/upgrade-eligibility"
    );

    return response;
  }

  /**
   * Create upgrade checkout session
   */
  async createUpgradeCheckout(uiMode: "embedded" | "hosted" = "embedded"): Promise<any> {
    const response = await this.makeAuthenticatedRequest<any>(
      "/payment/upgrade-checkout",
      {
        method: "POST",
        body: JSON.stringify({ ui_mode: uiMode })
      }
    );

    if (!response.success && !response.client_secret && !response.checkout_url) {
      throw new Error(response.error || "Failed to create upgrade checkout");
    }

    return response;
  }

  // Downloads Methods (New Simplified System)

  /**
   * Get user's available downloads based on their tier
   */
  async getUserDownloads(): Promise<UserDownload[]> {
    const response = await this.makeAuthenticatedRequest<DownloadsResponse>(
      `/downloads`
    );
    return response.downloads || [];
  }

  /**
   * Get download URL for specific download
   */
  async getDownloadUrl(downloadId: string): Promise<string> {
    const response = await this.makeAuthenticatedRequest<DownloadUrlResponse>(
      `/downloads/${downloadId}/url`
    );
    return response.downloadUrl;
  }

  // Payment Methods

  /**
   * Create Stripe checkout session
   */
  async createCheckoutSession(request: {
    sku: string;
    customer_email?: string;
    ui_mode?: "embedded" | "hosted" | "custom";
  }): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/api/payment/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Checkout failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get checkout session status
   */
  async getCheckoutSessionStatus(
    sessionId: string,
    immediateAccess: boolean = false
  ): Promise<any> {
    try {
      const url = immediateAccess
        ? `${this.baseUrl}/api/payment/session/${sessionId}?immediate_access=true`
        : `${this.baseUrl}/api/payment/session/${sessionId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Session status failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();
export type { UserInfo, CustomerProfile };
